import { ArrowRight, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { ConnectorType } from '@/types'
import {
  CONNECTOR_DESCRIPTIONS,
  CONNECTOR_LABELS,
  isAiProviderConnector,
  usesInstanceManagement,
} from './connectorConstants'
import { CONNECTOR_ICONS } from './connectorIcons'

export type ConnectorCardSection = 'enabled' | 'available'

interface ConnectorCardProps {
  connectorType: ConnectorType
  section: ConnectorCardSection
  canManage: boolean
  onClick: () => void
  onAddClick: () => void
  isConnectedOverride?: boolean
  hasDisabledInstances?: boolean
  enabledInstanceCount?: number
  unavailableReason?: string
}

export function ConnectorCard({
  connectorType,
  section,
  canManage,
  onClick,
  onAddClick,
  isConnectedOverride,
  hasDisabledInstances = false,
  enabledInstanceCount = 0,
  unavailableReason,
}: Readonly<ConnectorCardProps>) {
  const Icon = CONNECTOR_ICONS[connectorType]
  const label = CONNECTOR_LABELS[connectorType]
  const isConnected = isConnectedOverride ?? enabledInstanceCount > 0

  const heading = (
    <div className="flex flex-col">
      <h3 className="font-semibold">{label}</h3>
      {isAiProviderConnector(connectorType) && (
        <span className="text-[11px] font-bold uppercase tracking-wider text-primary">AI Provider</span>
      )}
    </div>
  )

  const icon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
      <Icon className="h-5 w-5" />
    </div>
  )

  if (usesInstanceManagement(connectorType)) {
    return (
      <Card
        className="relative p-6 flex flex-col gap-4 transition-all hover:bg-muted/50 hover:shadow-lg"
        data-testid={`connector-card-${connectorType}`}
        role="group"
        aria-label={`${label} connector${isConnected ? ' (connected)' : ''}`}
      >
        <div className="flex items-start justify-between">
          <button
            type="button"
            onClick={onClick}
            aria-label={`View ${label} details`}
            className="flex items-center gap-3 text-left after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring after:rounded-[inherit]"
            data-testid={`connector-card-${connectorType}-details`}
          >
            {icon}
            {heading}
          </button>

          <div className="relative z-10 flex items-center gap-1">
            {(section === 'enabled' || hasDisabledInstances) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClick}
                aria-label={`Manage ${label} connections`}
                data-testid={`connector-card-${connectorType}-manage`}
              >
                Manage
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
            {section === 'available' && canManage && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onAddClick}
                aria-label={`Add ${label} connection`}
                data-testid={`connector-card-${connectorType}-add`}
              >
                <Plus className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground flex-1 line-clamp-2">
          {CONNECTOR_DESCRIPTIONS[connectorType]}
        </p>

        {section === 'enabled' && (
          <span
            className="self-start rounded-full bg-muted px-3 py-1 text-xs font-semibold text-foreground"
            data-testid={`connector-card-${connectorType}-count`}
          >
            {enabledInstanceCount === 1 ? '1 connector' : `${enabledInstanceCount} connectors`}
          </span>
        )}
      </Card>
    )
  }

  const hasUnavailableReason = !!unavailableReason
  const isUnavailable = !isConnected && hasUnavailableReason
  const unavailableReasonId = `connector-card-${connectorType}-unavailable-reason`

  return (
    <Card
      className={cn(
        'p-6 flex flex-col gap-4 transition-all',
        isUnavailable ? 'cursor-default' : 'cursor-pointer hover:bg-muted/50 hover:shadow-lg',
      )}
      data-testid={`connector-card-${connectorType}`}
      role="button"
      aria-label={`${label} connector${isConnected ? ' (connected)' : ''}`}
      aria-disabled={isUnavailable || undefined}
      aria-describedby={hasUnavailableReason ? unavailableReasonId : undefined}
      onClick={isUnavailable ? undefined : onClick}
    >
      <div className={cn('flex items-start justify-between', isUnavailable && 'opacity-60')}>
        <div className="flex items-center gap-3">
          {icon}
          {heading}
        </div>

        {canManage && !isUnavailable && (isConnected ? (
          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground transition-colors">
            <X className="h-4 w-4" />
            <span>Disable</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground transition-colors">
            <Plus className="h-4 w-4" />
            <span>Enable</span>
          </div>
        ))}
      </div>

      <p className="text-sm text-muted-foreground flex-1 line-clamp-2">
        {CONNECTOR_DESCRIPTIONS[connectorType]}
      </p>

      {hasUnavailableReason && (
        <p
          id={unavailableReasonId}
          className="self-start rounded-full border px-3 py-1 text-xs font-semibold text-muted-foreground"
          data-testid={`connector-card-${connectorType}-unavailable`}
        >
          {unavailableReason}
        </p>
      )}
    </Card>
  )
}
